
import { useCallback, useEffect, useRef, useState } from 'react';

const LONG_PRESS_DELAY = 500;
const MOVE_THRESHOLD = 10;
const CLICK_SUPPRESS_MS = 700;
const HAPTIC_MS = 12;

export interface LongPressHandlers {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
  onClickCapture: (event: React.MouseEvent) => void;
}

export interface LongPressResult {
  pressing: boolean;
  handlers: LongPressHandlers;
}

function haptic(): void {
  try {
    navigator.vibrate?.(HAPTIC_MS);
  } catch {
  }
}

export function useLongPress(): LongPressResult {
  const [pressing, setPressing] = useState(false);
  const timerRef = useRef<number | null>(null);
  const suppressTimerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      if (suppressTimerRef.current !== null) window.clearTimeout(suppressTimerRef.current);
    },
    [],
  );

  const onTouchStart = useCallback(
    (event: React.TouchEvent) => {
      clearTimer();
      if (event.touches.length !== 1) {
        setPressing(false);
        startRef.current = null;
        return;
      }
      const touch = event.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
      setPressing(true);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setPressing(false);
        suppressClickRef.current = true;
        haptic();
        if (suppressTimerRef.current !== null) window.clearTimeout(suppressTimerRef.current);
        suppressTimerRef.current = window.setTimeout(() => {
          suppressClickRef.current = false;
          suppressTimerRef.current = null;
        }, CLICK_SUPPRESS_MS);
      }, LONG_PRESS_DELAY);
    },
    [clearTimer],
  );

  const onTouchMove = useCallback(
    (event: React.TouchEvent) => {
      const start = startRef.current;
      if (timerRef.current === null || !start || event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (
        Math.abs(touch.clientX - start.x) > MOVE_THRESHOLD ||
        Math.abs(touch.clientY - start.y) > MOVE_THRESHOLD
      ) {
        clearTimer();
        setPressing(false);
      }
    },
    [clearTimer],
  );

  const onTouchEnd = useCallback(() => {
    clearTimer();
    startRef.current = null;
    setPressing(false);
  }, [clearTimer]);

  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    if (suppressTimerRef.current !== null) {
      window.clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = null;
    }
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    pressing,
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd, onClickCapture },
  };
}
